import { useCallback, useEffect, useMemo, useState } from "react";
import { CalculatePaymentsLimitsPayload, LimitsStatus, LimitsStatusPeriods, PaymentType, v4Service } from "src/api";
import { useDepositWithdrawContext } from "src/contexts";

/** Tightest remaining headroom across a period block. Infinity = no cap. */
const minRemaining = (periods?: LimitsStatusPeriods | null): number => {
  if (!periods) return Infinity;
  const remaining = [periods.daily, periods.weekly, periods.monthly]
    .map((p) => p?.remaining)
    .filter((r): r is number => r != null);
  return remaining.length ? Math.min(...remaining) : Infinity;
};

/**
 * Loads the user's live payment limits (used/limit/remaining per period)
 * from vakotrade for the wallet currency and derives the tightest
 * remaining headroom (the "available limit"). Everything is denominated in
 * the wallet currency (passed as `notion_currency`). `availableLimit` is
 * `Infinity` when the limit group sets no cap — callers must treat that as
 * "unlimited" and not block.
 */
export const useCalculatedLimits = (paymentType: PaymentType) => {
  const { balance } = useDepositWithdrawContext();
  const currencyId = balance.currency.currency_id;
  const [limitsStatus, setLimitsStatus] = useState<LimitsStatus | null>(null);
  const [loading, setLoading] = useState(false);
  // Bumped to force a re-fetch (e.g. after a completed deposit/withdrawal
  // returns to the form — the used totals have moved server-side).
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!currencyId) return;
    let cancelled = false;
    setLoading(true);
    const payload: CalculatePaymentsLimitsPayload = {
      currency_id: currencyId,
      type: paymentType,
      notion_currency: currencyId,
    };
    v4Service
      .getCalculatedPaymentsLimits(payload)
      .then((status) => {
        if (!cancelled) setLimitsStatus(status);
      })
      .catch((e) => {
        console.error("Failed to load payment limits:", e);
        if (!cancelled) setLimitsStatus(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currencyId, paymentType, refreshKey]);

  const availableLimit = useMemo(
    () => Math.min(minRemaining(limitsStatus?.payments), minRemaining(limitsStatus?.aggregated)),
    [limitsStatus]
  );

  return { limitsStatus, availableLimit, loading, currencyId, refetch };
};
